import React, { useState, ChangeEvent } from 'react';
import { Box, InputAdornment, TextField, RadioGroup, FormControlLabel, Radio } from '@mui/material';
import { ISetupPdfMessages } from '../../../../types/setupPDFTypes';
import { DEFAULT_PAGE_MARGINS, IPublicationSetupPdfStyle } from '@opengov/stories-shared/constants';

import { styles } from './PageMargins.styles';

export enum PageMarginsValue {
  DEFAULT = 'default',
  CUSTOM = 'custom'
}

export const MAX_PAGE_MARGIN = 1.75;
export const MIN_PAGE_MARGIN = 0.25;

export type IPageMargins = IPublicationSetupPdfStyle['pageMargins'];

export const PageMargins = ({
  messages,
  pageMargins,
  onPageMarginsChange
}: {
  messages: ISetupPdfMessages;
  pageMargins: IPageMargins | null;
  onPageMarginsChange: (value: PageMarginsValue, customMargins: IPageMargins) => void;
}) => {
  const [value, setValue] = useState(pageMargins ? PageMarginsValue.CUSTOM : PageMarginsValue.DEFAULT);
  const [customMargins, setCustomMargins] = useState(pageMargins || DEFAULT_PAGE_MARGINS);

  const handleCustomMarginsChange = (event: ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;

    if (value === '') {
      setCustomMargins((prev) => ({ ...prev, [name]: MIN_PAGE_MARGIN.toString() }));
      onPageMarginsChange(PageMarginsValue.CUSTOM, { ...customMargins, [name]: MIN_PAGE_MARGIN.toString() });
      return;
    }

    if (/^\d*\.?\d*$/.test(value)) {
      let sanitized = value;

      if (/^0\d+/.test(sanitized)) {
        sanitized = sanitized.replace(/^0+/, '');
      }
      if (Number(sanitized) > MAX_PAGE_MARGIN) {
        sanitized = MAX_PAGE_MARGIN.toString();
      }
      if (Number(sanitized) < MIN_PAGE_MARGIN) {
        sanitized = MIN_PAGE_MARGIN.toString();
      }
      const newCustomMargins = { ...customMargins, [name]: sanitized };
      setCustomMargins(newCustomMargins);
      onPageMarginsChange(PageMarginsValue.CUSTOM, newCustomMargins);
    }
  };

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    setValue(event.target.value as PageMarginsValue);
    if (event.target.value === PageMarginsValue.DEFAULT) {
      setCustomMargins(DEFAULT_PAGE_MARGINS);
    }
    onPageMarginsChange(event.target.value as PageMarginsValue, customMargins);
  };

  return (
    <Box sx={styles.container}>
      <RadioGroup
        aria-labelledby="page-margins-radio-buttons-group-label"
        name="page-margins-radio-buttons-group"
        value={value}
        onChange={handleChange}
      >
        <FormControlLabel
          value={PageMarginsValue.DEFAULT}
          control={<Radio inputProps={styles.radioButton} />}
          label={messages.default}
        />
        <FormControlLabel
          value={PageMarginsValue.CUSTOM}
          control={<Radio inputProps={styles.radioButton} />}
          label={messages.custom}
        />
      </RadioGroup>

      <Box sx={styles.customMargins}>
        {(['top', 'bottom', 'left', 'right'] as Array<keyof IPageMargins>).map((key) => (
          <TextField
            key={key}
            type="number"
            inputProps={{
              step: 0.1,
              min: MIN_PAGE_MARGIN,
              max: MAX_PAGE_MARGIN
            }}
            size="small"
            label={messages[key as keyof ISetupPdfMessages] as string}
            name={key}
            InputProps={{
              endAdornment: <InputAdornment position="end">in</InputAdornment>
            }}
            value={customMargins[key]}
            onChange={handleCustomMarginsChange}
            sx={styles.customMarginsInput}
            disabled={value !== PageMarginsValue.CUSTOM}
          />
        ))}
      </Box>
    </Box>
  );
};
